// Client-side helpers for the non-payment account block.
//
// The server decides WHETHER an account is blocked (app/helpers/system_helper.php,
// evaluateCustomerBlock) so the cobro and central apps can never drift apart and so the
// cutoff day is evaluated in the server's America/Santo_Domingo timezone rather than
// whatever the device is set to. Nothing here re-derives that decision - it only turns
// the server's answer into a UI state and some Spanish copy.
//
// Kept free of React so it can be unit tested under vitest's node environment.

export const BLOCK_STATUS = {
  LOADING: "loading",
  BLOCKED: "blocked",
  ALLOWED: "allowed",
  UNKNOWN: "unknown",
};

export const MONTHS_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/**
 * "2026-08-21" -> "21 de agosto". Returns null on anything unparseable so callers can
 * fall back to copy without a date instead of rendering "el undefined".
 */
export const formatCutoffDate = (isoDate) => {
  if (typeof isoDate !== "string") return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!match) return null;

  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  return `${day} de ${MONTHS_ES[month - 1]}`;
};

/**
 * Turn the SWR result for /customer/balance into a guard state.
 *
 * `customer` is undefined while the request is in flight, and literally false when the
 * owner has no customer_balance row (never billed, so never blocked).
 *
 * We fail CLOSED while waiting - a blocked user must not get a usable form for the split
 * second before the answer lands. But we fail OPEN when offline or when the request has
 * been hanging past the timeout: the shared fetcher swallows errors and returns undefined
 * forever on failure, so without that bound a flaky network would leave every guarded
 * page spinning for good.
 */
export const resolveBlockStatus = ({ ownerId, customer, isOnline = true, timedOut = false }) => {
  if (!ownerId) return BLOCK_STATUS.UNKNOWN;

  if (customer === undefined) {
    if (!isOnline || timedOut) return BLOCK_STATUS.UNKNOWN;
    return BLOCK_STATUS.LOADING;
  }

  if (!customer) return BLOCK_STATUS.ALLOWED;

  return customer.isDisabled ? BLOCK_STATUS.BLOCKED : BLOCK_STATUS.ALLOWED;
};

/**
 * Whether the customer owes too much to grant themselves a temporary self-unblock.
 *
 * The rule: a balance above a single month's payment means prior unpaid debt (balance is
 * prevBal + the current invoice), so the account is too far behind for a courtesy unblock
 * and has to be paid down instead. Strictly greater - owing exactly one month still
 * qualifies.
 *
 * Both fields are coerced: `monthly` arrives as a string ("500") and `balance` as a
 * number, so a raw `>` would compare "1000" > "500" as strings and silently pass.
 *
 * Returns false (i.e. does not restrict) when the payload is missing or `monthly` is not
 * a positive number - an API rollback that drops the field must not lock every customer
 * out of the page. The exception-count and active-exception guards still apply on top of
 * this, and the server remains the authority on the block itself.
 */
export const exceedsMonthlyForUnblock = (customer) => {
  if (!customer) return false;

  const monthly = +customer.monthly;
  const balance = +customer.balance;

  if (!Number.isFinite(monthly) || monthly <= 0) return false;
  if (!Number.isFinite(balance)) return false;

  return balance > monthly;
};

/**
 * Banner copy for an owner who owes money.
 *
 * Returns null when there is nothing to say, including when the payload predates the
 * block fields (an API rollback) - in that case we would rather show no banner than a
 * half-rendered sentence.
 *
 * `willBeDisabled` is the rule's own outcome and is true even while enforcement is still
 * switched off, which is what powers the countdown before the feature goes live.
 */
export const getBlockWarning = (customer) => {
  if (!customer) return null;

  const cutoff = formatCutoffDate(customer.blockCutoffDate);
  const owesMoney = +customer.balance > 0 || customer.willBeDisabled || customer.isDisabled;

  if (!owesMoney) return null;

  if (customer.isDisabled) {
    return {
      severity: "error",
      title: "Cuenta bloqueada",
      text:
        customer.blockReason === "cutoff" && cutoff
          ? `Su cuenta fue bloqueada el ${cutoff} por falta de pago!`
          : "Cuenta bloqueada por falta de pagos!",
    };
  }

  // A reason that survives with BOTH flags false is the signature of an active
  // self-unblock: the server clears isDisabled and willBeDisabled but keeps the reason.
  // (Reason set with willBeDisabled still true is the flag-off countdown instead, which
  // the cutoff-date copy below handles correctly.)
  //
  // Naming the cutoff date here would be wrong twice: the "months" rule never waits for
  // the cutoff, and either way the block returns the moment the exception lapses, which
  // can be hours away. Deliberately gives no date rather than one we cannot state
  // correctly from the client.
  if (customer.blockReason && !customer.willBeDisabled) {
    return {
      severity: "error",
      title: "Desbloqueo temporal",
      text: "Su cuenta volvera a bloquearse al vencer el desbloqueo temporal!",
    };
  }

  // The rule already fires; only the rollout flag is holding the block back.
  if (customer.willBeDisabled && cutoff) {
    return {
      severity: "error",
      title: "Balance Pendiente!",
      text: `Su cuenta sera bloqueada el ${cutoff} por falta de pago!`,
    };
  }

  if (cutoff) {
    const days = +customer.isDisabledDays;
    return {
      severity: "warning",
      title: "Balance Pendiente!",
      // An absolute date stays correct in a stale cache; "en 3 dias" does not.
      text: `Su cuenta sera bloqueada el ${cutoff}${days > 0 ? ` (en ${days} dia${days === 1 ? "" : "s"})` : ""}!`,
    };
  }

  if (!(+customer.balance > 0)) return null;

  return {
    severity: "warning",
    title: "Balance Pendiente!",
    text: "Usted tiene pago(s) vencido, por favor evite atraso!",
  };
};
